import {
  AdminGetRoutesResDto,
  AdminGetDateRouteNotificationsResDto,
  AdminGetRouteNotificationsResDto,
} from "service/api/estabrookSchemas";
import { NotificationTypes } from "../../features/route-planning/types/notification-types-enum";

export type NotificationState = "active" | "disabled" | "custom";

export interface NotificationSettings {
  eta: boolean;
  serviceDelayed: boolean;
  statusUpdate: boolean;
  minutesBeforeService: boolean;
  serviceModified: boolean;
}

/**
 * Determines the notification state for a single route with hierarchy check
 * @param route - The route data with notification settings
 * @param globalSettings - Global notification settings (takes precedence)
 * @returns NotificationState - "active", "disabled", or "custom"
 */
export const getRouteNotificationState = (
  route: AdminGetRoutesResDto,
  globalSettings?: AdminGetDateRouteNotificationsResDto,
): NotificationState => {
  // If global is enabled or not set, check route-specific settings
  const settings = route.notificationSettings;

  if (!settings) {
    // If no notification settings exist, default to disabled
    return "disabled";
  }

  const {
    eta,
    serviceDelayed,
    statusUpdate,
    minutesBeforeService,
    serviceModified,
  } = settings;

  const minutesBeforeServiceEnabled = minutesBeforeService > 0;

  // Check if all notifications are disabled (disabled state)
  if (
    !eta &&
    !serviceDelayed &&
    !statusUpdate &&
    !minutesBeforeServiceEnabled &&
    !serviceModified
  ) {
    return "disabled";
  }

  // Hierarchy: Check global state first
  const globalState = getGlobalNotificationStateFromAPI(globalSettings);

  // If global notifications are disabled, all routes are disabled
  if (globalState === "disabled") {
    return "disabled";
  }

  // Check if all notifications are enabled (active state)
  if (
    eta &&
    serviceDelayed &&
    statusUpdate &&
    minutesBeforeServiceEnabled &&
    serviceModified &&
    globalState === "active"
  ) {
    return "active";
  }

  // Mixed state (custom)
  return "custom";
};

/**
 * Determines the global notification state based on all routes
 * @param routes - Array of route data
 * @param globalSettings - Global notification settings for hierarchy
 * @returns NotificationState - "active", "disabled", or "custom"
 */
export const getGlobalNotificationState = (
  routes: AdminGetRoutesResDto[],
  globalSettings?: AdminGetDateRouteNotificationsResDto,
): NotificationState => {
  if (!routes || routes.length === 0) {
    return "disabled";
  }

  const routeStates = routes.map((route) =>
    getRouteNotificationState(route, globalSettings),
  );

  const uniqueStates = [...new Set(routeStates)];

  // If all routes have the same state, return that state
  if (uniqueStates.length === 1) {
    return uniqueStates[0];
  }

  // If routes have mixed states, or if any route has custom state, return custom
  return "custom";
};

/**
 * Determines the global notification state from global API settings
 * @param settings - Global notification settings from API
 * @returns NotificationState - "active", "disabled", or "custom"
 */
export const getGlobalNotificationStateFromAPI = (
  settings?: AdminGetDateRouteNotificationsResDto,
): NotificationState => {
  if (!settings) {
    return "disabled";
  }

  const {
    eta,
    serviceDelayed,
    statusUpdate,
    minutesBeforeService,
    serviceModified,
  } = settings;

  // Check if all notifications are enabled (active state)
  if (
    eta &&
    serviceDelayed &&
    statusUpdate &&
    minutesBeforeService &&
    serviceModified
  ) {
    return "active";
  }

  // Check if all notifications are disabled (disabled state)
  if (
    !eta &&
    !serviceDelayed &&
    !statusUpdate &&
    !minutesBeforeService &&
    !serviceModified
  ) {
    return "disabled";
  }

  // Mixed state (custom)
  return "custom";
};

/**
 * Converts API notification settings to UI format for the modal (for route-level settings)
 * @param settings - API notification settings
 * @returns Object with notification types as keys and boolean values
 */
export const apiSettingsToUIFormat = (
  settings?: AdminGetRoutesResDto["notificationSettings"],
): Record<string, boolean> => {
  if (!settings) {
    return {
      [NotificationTypes.ROUTE_DISPATCHED]: false,
      [NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE]: false,
      [NotificationTypes.SERVICE_DELAYED]: false,
      [NotificationTypes.STATUS_UPDATES]: false,
      [NotificationTypes.SERVICE_MODIFIED]: false,
    };
  }

  return {
    [NotificationTypes.ROUTE_DISPATCHED]: settings.eta,
    [NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE]:
      settings.minutesBeforeService > 0,
    [NotificationTypes.SERVICE_DELAYED]: settings.serviceDelayed,
    [NotificationTypes.STATUS_UPDATES]: settings.statusUpdate,
    [NotificationTypes.SERVICE_MODIFIED]: settings.serviceModified,
  };
};

/**
 * Converts global API notification settings to UI format for the modal
 * @param settings - Global API notification settings
 * @returns Object with notification types as keys and boolean values
 */
export const globalApiSettingsToUIFormat = (settings?: {
  eta: boolean;
  serviceDelayed: boolean;
  statusUpdate: boolean;
  minutesBeforeService: boolean;
  serviceModified?: boolean;
}): Record<string, boolean> => {
  if (!settings) {
    return {
      [NotificationTypes.ROUTE_DISPATCHED]: false,
      [NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE]: false,
      [NotificationTypes.SERVICE_DELAYED]: false,
      [NotificationTypes.STATUS_UPDATES]: false,
      [NotificationTypes.SERVICE_MODIFIED]: false,
    };
  }

  return {
    [NotificationTypes.ROUTE_DISPATCHED]: settings.eta,
    [NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE]:
      settings.minutesBeforeService,
    [NotificationTypes.SERVICE_DELAYED]: settings.serviceDelayed,
    [NotificationTypes.STATUS_UPDATES]: settings.statusUpdate,
    [NotificationTypes.SERVICE_MODIFIED]: settings.serviceModified ?? false,
  };
};

/**
 * Universal converter that handles both global and route notification settings
 * @param settings - Either global or route notification settings
 * @returns Object with notification types as keys and boolean values
 */
export const universalApiSettingsToUIFormat = (
  settings?:
    | AdminGetDateRouteNotificationsResDto
    | AdminGetRouteNotificationsResDto,
): Record<string, boolean> => {
  if (!settings) {
    return {
      [NotificationTypes.ROUTE_DISPATCHED]: false,
      [NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE]: false,
      [NotificationTypes.SERVICE_DELAYED]: false,
      [NotificationTypes.STATUS_UPDATES]: false,
      [NotificationTypes.SERVICE_MODIFIED]: false,
    };
  }

  return {
    [NotificationTypes.ROUTE_DISPATCHED]: settings.eta,
    [NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE]:
      typeof settings.minutesBeforeService === "boolean"
        ? settings.minutesBeforeService
        : settings.minutesBeforeService > 0,
    [NotificationTypes.SERVICE_DELAYED]: settings.serviceDelayed,
    [NotificationTypes.STATUS_UPDATES]: settings.statusUpdate,
    [NotificationTypes.SERVICE_MODIFIED]: settings.serviceModified,
  };
};

/**
 * Converts UI format to API format for saving
 * @param uiSettings - UI notification settings from modal
 * @returns API-compatible notification settings
 */
export const uiSettingsToAPIFormat = (
  uiSettings: Record<string, boolean>,
): NotificationSettings => {
  return {
    eta: uiSettings[NotificationTypes.ROUTE_DISPATCHED] ?? false,
    serviceDelayed: uiSettings[NotificationTypes.SERVICE_DELAYED] ?? false,
    statusUpdate: uiSettings[NotificationTypes.STATUS_UPDATES] ?? false,
    minutesBeforeService:
      uiSettings[NotificationTypes.FIFTEEN_MINUTES_BEFORE_SERVICE] ?? false,
    serviceModified: uiSettings[NotificationTypes.SERVICE_MODIFIED] ?? false,
  };
};

/**
 * Creates "all enabled" notification settings
 */
export const getAllEnabledSettings = (): NotificationSettings => ({
  eta: true,
  serviceDelayed: true,
  statusUpdate: true,
  minutesBeforeService: true,
  serviceModified: true,
});

/**
 * Creates "all disabled" notification settings
 */
export const getAllDisabledSettings = (): NotificationSettings => ({
  eta: false,
  serviceDelayed: false,
  statusUpdate: false,
  minutesBeforeService: false,
  serviceModified: false,
});
